import { useEffect, useState } from "react";
import axios from "axios";
import { useDispatch } from "react-redux";
import { useParams, useNavigate } from "react-router-dom";
import { ArrowRight, Download, Share2, FileText } from "lucide-react";
import RepairEstimateModal from "../../components/RepairEstimateModal";
import AlertDialogModal from "../../components/Dialog";
import {
  FormControl,
  FormControlLabel,
  Radio,
  RadioGroup,
  Snackbar,
} from "@mui/material";
import Button from "../../components/Button";
import Loader from "../../components/Loader";
import { FaArrowLeft } from "react-icons/fa";
import MediaViewer from "../../components/MediaViewer";
import SlideViewModal from "../../components/SlideViewer";
import { getErrorMessage } from "../../utils/error";
import { Pencil } from "lucide-react";
import {
  MANAGER,
  ADMIN,
  APPUSER,
  SUPER_ADMIN,
  VISO_SUPER_ADMIN,
  DASHBOARD_EDITOR,
} from "../../constants";
import { Dialog, IconButton } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import {
  getScanStageReportDetails,
  updateDefectSignificance,
  updateReportedToManager,
  updateReportPublished,
  getScanPdf,
  getScanImagesZip,
  updateScanInfo,
  updateScanStageInfo,
} from "./api";
import { errorMessage, successMessage } from "../../components/Toaster";
import {
  updateScanStageStatus,
  updateScanContainerNumber,
} from "../../store/slices/reportSlice";

// ── Image list builder — no download, no canvas, direct URL ──────────────────
function buildImageList(imageList) {
  return imageList.map((img) => ({
    thumb: img.file_url,
    original: img.file_url,
    file_name: img.file_name,
  }));
}

// ── ReportDetails ─────────────────────────────────────────────────────────────

const ReportDetails = () => {
  const { id, key } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const decodedKey = decodeURIComponent(key || "");
  const [scanId, stageName] = decodedKey.split("_");
  const roleName = localStorage.getItem("role_name");
  const UserId = localStorage.getItem("user_id");
  const location_map = new Map();

  const [isLoading, setIsLoading] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [reportData, setReportData] = useState({});
  const [defects, setDefects] = useState([]);
  const [addressData, setAddressData] = useState("-");
  const [insignificant, setInsignificant] = useState(false);
  const [checkAll, setCheckAll] = useState(false);
  const [viewType, setViewType] = useState("images");
  const [imageType, setImageType] = useState("raw");
  const [compressedRawImages, setCompressedRawImages] = useState([]);
  const [compressedProcessedImages, setCompressedProcessedImages] = useState(
    [],
  );
  const [compressedHeatmapImages, setCompressedHeatmapImages] = useState([]);
  const [compressedSegmentedImages, setCompressedSegmentedImages] = useState(
    [],
  );
  const [compressedContainerNumberImages, setCompressedContainerNumberImages] =
    useState([]);
  const [compressedIsoNumberImages, setCompressedIsoNumberImages] = useState(
    [],
  );
  const [compressedPlateImages, setCompressedPlateImages] = useState([]);
  const [compressedSealCodeImages, setCompressedSealCodeImages] = useState([]);
  const [compressedVehicleNumberImages, setCompressedVehicleNumberImages] =
    useState([]);
  const [isSharing, setIsSharing] = useState(false);
  const [slideViewOpen, setSlideViewOpen] = useState(false);
  const [estimateOpen, setEstimateOpen] = useState(false);
  const [currentDefects, setCurrentDefects] = useState(
    defects.filter((defect) =>
      insignificant
        ? !defect.is_significant_defect
        : defect.is_significant_defect,
    ),
  );
  const [open, setOpen] = useState(false);

  // Edit form fields
  const [containerInput, setContainerInput] = useState("");
  const [isoInput, setIsoInput] = useState("");
  const [stageInput, setStageInput] = useState("");
  const [capacityInput, setCapacityInput] = useState("");
  const [vehicleNoInput, setVehicleNoInput] = useState("");
  const [maxGrossWeightInput, setMaxGrossWeightInput] = useState("");
  const [isUpdating, setIsUpdating] = useState(false);

  const [openContainerEdit, setOpenContainerEdit] = useState(false);
  const [quickContainerInput, setQuickContainerInput] = useState("");
  const [quickIsoInput, setQuickIsoInput] = useState("");
  const [quickVehicleInput, setQuickVehicleInput] = useState("");
  const [isQuickUpdating, setIsQuickUpdating] = useState(false);
  const [confirmQuickUpdateOpen, setConfirmQuickUpdateOpen] = useState(false);

  const ASSET_LABELS = {
    Container: {
      reportTitle: "Container Inspection Report",
      idLabel: "Container ID",
      capacityLabel: "Container Capacity",
      typeLabel: "Container Type",
      codeLabel: "ISO Code",
      numberLabel: "Container",
      codePlaceholder: "e.g. 22G1",
    },
    IsoTank: {
      reportTitle: "Tank Inspection Report",
      idLabel: "Tank ID",
      capacityLabel: "Tank Capacity",
      typeLabel: "Tank Type",
      codeLabel: "Tank Code",
      numberLabel: "Tank",
      codePlaceholder: "e.g. T001",
    },
    Other: {
      reportTitle: "Asset Inspection Report",
      idLabel: "Asset ID",
      capacityLabel: "Asset Capacity",
      typeLabel: "Asset Type",
      codeLabel: "ISO Code",
      numberLabel: "Container",
      codePlaceholder: "e.g. 22G1",
    },
  };
  const assetType = localStorage.getItem("asset_type") || "Container";
  const labels = ASSET_LABELS[assetType] || ASSET_LABELS["Container"];

  const getGrossWeightFromIso = (iso) => {
    if (!iso) return "";
    const upper = iso.toUpperCase();
    if (upper === "22G1" || upper.startsWith("22")) return "30,480 kg";
    if (upper === "45R1") return "35,000 kg";
    if (upper === "45G1" || upper.startsWith("45")) return "32,500 kg";
    return "";
  };

  useEffect(() => {
    fetchReportDetails();
  }, [id]);

  useEffect(() => {
    if (reportData?.video) {
      setViewType("video");
    } else if (reportData?.scan_images && reportData.scan_images.length > 0) {
      setViewType("images");
    }
  }, [reportData]);

  useEffect(() => {
    const scanImages = reportData.scan_images;
    if (!scanImages?.length) return;

    const rawImages = scanImages.filter((img) =>
      img.file_name?.toLowerCase().includes("original"),
    );
    const processedImages = scanImages.filter((img) =>
      img.file_name?.toLowerCase().includes("annotated"),
    );
    const heatmapImages = scanImages.filter((img) =>
      img.file_name?.toLowerCase().includes("heatmap"),
    );
    const segmentedImages = scanImages.filter((img) =>
      img.file_name?.toLowerCase().includes("area"),
    );
    const containerNumberImages = scanImages.filter((img) => {
      const fn = img.file_name?.toLowerCase() || "";
      return fn.includes("container_number") || fn.includes("tank_number");
    });
    const isoNumberImages = scanImages.filter((img) =>
      img.file_name?.toLowerCase().includes("iso_number"),
    );
    const plateImages = scanImages.filter((img) =>
      img.file_name?.toLowerCase().includes("plate"),
    );
    const sealCodeImages = scanImages.filter((img) =>
      img.file_name?.toLowerCase().includes("seal_code"),
    );

    const vehicleNumberImages = scanImages.filter((img) => {
      const fn = img.file_name?.toLowerCase() || "";
      return fn.includes("vehicle_number");
    });

    if (vehicleNumberImages.length)
      setCompressedVehicleNumberImages(buildImageList(vehicleNumberImages));
    if (rawImages.length) setCompressedRawImages(buildImageList(rawImages));
    if (processedImages.length)
      setCompressedProcessedImages(buildImageList(processedImages));
    if (heatmapImages.length)
      setCompressedHeatmapImages(buildImageList(heatmapImages));
    if (segmentedImages.length)
      setCompressedSegmentedImages(buildImageList(segmentedImages));
    if (containerNumberImages.length)
      setCompressedContainerNumberImages(buildImageList(containerNumberImages));
    if (isoNumberImages.length)
      setCompressedIsoNumberImages(buildImageList(isoNumberImages));
    if (plateImages.length)
      setCompressedPlateImages(buildImageList(plateImages));
    if (sealCodeImages.length)
      setCompressedSealCodeImages(buildImageList(sealCodeImages));
  }, [reportData.scan_images]);

  const fetchReportDetails = async () => {
    if (!id) return;
    setIsLoading(true);
    try {
      const res = await getScanStageReportDetails(id);
      setReportData(res.data);
      setDefects(res.data.defects);
      if (res.data.latitude && res.data.longitude) {
        const geoRes = await reverseGeoSearch(
          res.data.latitude,
          res.data.longitude,
        );
        setAddressData(geoRes?.display_name || "-");
      }
    } catch (err) {
      console.error("Failed to load report data", err);
      errorMessage(getErrorMessage(err) || "Failed to load report data.");
    }
    setIsLoading(false);
  };

  useEffect(() => {
    setCurrentDefects(
      defects
        .filter((defect) =>
          insignificant
            ? !defect.is_significant_defect
            : defect.is_significant_defect,
        )
        .map((defect) => ({ ...defect, isChecked: false })),
    );
  }, [defects, insignificant]);

  useEffect(() => {
    if (checkAll) {
      setCurrentDefects((prev) => prev.map((d) => ({ ...d, isChecked: true })));
    } else {
      setCurrentDefects((prev) =>
        prev.map((d) => ({ ...d, isChecked: false })),
      );
    }
  }, [checkAll]);

  const handleTabChange = (val) => {
    setInsignificant(val);
    setCheckAll(false);
  };

  const handleCheck = (defectId, checked) => {
    setCurrentDefects((prev) =>
      prev.map((d) =>
        d.defect_id === defectId ? { ...d, isChecked: checked } : d,
      ),
    );
  };

  const reverseGeoSearch = async (lat, lon) => {
    try {
      const key = `${lat},${lon}`;
      if (location_map.has(key)) return location_map.get(key);
      const response = await axios.get(
        `https://nominatim.openstreetmap.org/reverse?format=json&lat=${lat}&lon=${lon}&zoom=18&addressdetails=0`,
      );
      location_map.set(key, response.data);
      return response.data;
    } catch (error) {
      console.error("Error in reverseGeoSearch:", error);
      return null;
    }
  };

  const moveToInsignificant = async () => {
    try {
      const insignificantDefectIds = currentDefects
        .filter((d) => d.isChecked)
        .map((d) => parseInt(d.defect_id));
      await updateDefectSignificance({
        defect_ids: insignificantDefectIds,
        is_significant_defect: insignificant,
        reviewer_id: UserId,
        scan_stage_id: reportData.scan_stage_id,
      });
      setDefects((prev) =>
        prev.map((d) =>
          insignificantDefectIds.includes(parseInt(d.defect_id))
            ? { ...d, is_significant_defect: insignificant }
            : d,
        ),
      );
    } catch (err) {
      errorMessage(getErrorMessage(err) || "Failed to move data.");
    }
  };

  const handleReportToManager = async () => {
    if (!id) return;
    setIsLoading(true);
    try {
      const res = await updateReportedToManager({
        scan_stage_id: id,
        reported_to_manager: true,
      });
      successMessage(res.message || "Report sent to manager successfully.");
      dispatch(
        updateScanStageStatus({ scanId, stageName, reported_to_manager: true }),
      );
      await fetchReportDetails();
    } catch (err) {
      errorMessage(getErrorMessage(err) || "Failed to report to manager.");
    }
    setIsLoading(false);
  };

  const handlePublishReport = async () => {
    if (!id) return;
    setIsLoading(true);
    try {
      const res = await updateReportPublished({
        scan_stage_id: id,
        report_published: true,
      });
      successMessage(res.message || "Report published successfully.");
      dispatch(
        updateScanStageStatus({ scanId, stageName, report_published: true }),
      );
      await fetchReportDetails();
    } catch (err) {
      errorMessage(getErrorMessage(err) || "Failed to publish report.");
    }
    setIsLoading(false);
  };

  const downloadReport = async () => {
    if (!scanId) return;
    setSnackbarOpen(true);
    try {
      await getScanPdf(scanId, stageName);
    } catch (error) {
      errorMessage(getErrorMessage(error) || "Failed to download report.");
    }
    setSnackbarOpen(false);
  };

  const handleImageTypeTab = (type) => setImageType(type);

  const shareReport = async (reportData) => {
    setIsSharing(true);
    const containerNumber = reportData?.container_number || "N/A";
    const images = reportData?.scan_images || [];
    const videos = reportData?.video || [];
    const today = new Date();
    const formattedDate = today.toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
    let bodyText = `Hi,\n\nThe report for the ${labels.idLabel} ${containerNumber} is shared below.\n\n`;
    try {
      if (images.length > 0) {
        const zipRes = await getScanImagesZip(reportData.scan_stage_id);
        if (zipRes?.status && zipRes?.data) {
          bodyText += `Images:\nSurvey_Images_${containerNumber}.zip\n${zipRes.data}\n\n`;
        } else {
          bodyText += `Images archive could not be generated.\n\n`;
        }
      } else if (videos.length > 0) {
        bodyText += `Video file:\n${videos[0].file_name}\n${videos[0].file_url}\n\n`;
      } else {
        bodyText += `No files available for this ${labels.idLabel}.\n\n`;
      }
    } catch (err) {
      bodyText += `Report files could not be generated.\n\n`;
    }
    bodyText += `Note: The shared download link will remain active for one month from ${formattedDate}.\n\nThanks`;
    const subject = encodeURIComponent(
      `Survey Report For ${labels.numberLabel} Number ${containerNumber}`,
    );
    const body = encodeURIComponent(bodyText);
    const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
    if (isMobile) {
      window.location.href = `mailto:?subject=${subject}&body=${body}`;
    } else {
      window.open(
        `https://mail.google.com/mail/?view=cm&fs=1&su=${subject}&body=${body}`,
        "_blank",
      );
    }
    setIsSharing(false);
  };

  const getCurrentImages = () => {
    switch (imageType) {
      case "raw":
        return compressedRawImages;
      case "processed":
        return compressedProcessedImages;
      case "heatmap":
        return compressedHeatmapImages;
      case "segmented":
        return compressedSegmentedImages;
      case "container_number":
      case "tank_number":
        return compressedContainerNumberImages;
      case "iso_number":
        return compressedIsoNumberImages;
      case "plate":
        return compressedPlateImages;
      case "seal_code":
        return compressedSealCodeImages;
      case "vehicle_number":
        return compressedVehicleNumberImages;
      default:
        return [];
    }
  };

  const handleDefectsUpdated = (newList) => {
    if (newList) setDefects(newList);
    fetchReportDetails();
  };

  const handleContainerUpdated = (newContainerNumber) => {
    dispatch(
      updateScanContainerNumber({
        scanId,
        container_number: newContainerNumber,
      }),
    );
    fetchReportDetails();
  };

  const handleContainerInputChange = (e) =>
    setContainerInput(e.target.value.toUpperCase());

  const handleIsoInputChange = (e) => {
    const value = e.target.value.toUpperCase();
    if (value.length > 4) return;
    if (/^[A-Z0-9]*$/.test(value)) {
      setIsoInput(value);
      const autoWeight = getGrossWeightFromIso(value);
      if (autoWeight) setMaxGrossWeightInput(autoWeight);
    }
  };

  const resetForm = () => {
    setContainerInput("");
    setIsoInput("");
    setCapacityInput("");
    setStageInput("");
    setVehicleNoInput("");
    setMaxGrossWeightInput("");
  };

  const resetQuickForm = () => {
    setQuickContainerInput("");
    setQuickIsoInput("");
    setQuickVehicleInput("");
  };

  const handleupdateScanInfo = async () => {
    if (
      containerInput.trim().length === 0 &&
      isoInput.trim().length === 0 &&
      stageInput.length === 0 &&
      capacityInput.length === 0 &&
      vehicleNoInput.trim().length === 0 &&
      maxGrossWeightInput.trim().length === 0
    )
      return;

    setIsUpdating(true);
    try {
      const payload = {
        scan_id: scanId,
        scan_stage_id: id,
        container_number:
          containerInput.trim().length > 0
            ? containerInput.trim()
            : reportData?.container_number,
        iso_code:
          isoInput.trim().length > 0 ? isoInput.trim() : reportData?.iso_code,
        container_capacity:
          capacityInput.length > 0
            ? capacityInput
            : reportData?.container_capacity,
        stage_name: stageInput.length > 0 ? stageInput : reportData?.stage_name,
        vehicle_no:
          vehicleNoInput.trim().length > 0
            ? vehicleNoInput.trim()
            : reportData?.vehicle_no,
        max_gross_weight:
          maxGrossWeightInput.trim().length > 0
            ? maxGrossWeightInput.trim()
            : reportData?.max_gross_weight,
      };
      await updateScanInfo(payload);
      successMessage("Updated successfully.");
      resetForm();
      handleContainerUpdated(payload.container_number);
      setOpen(false);
    } catch (err) {
      errorMessage("Failed to update.");
    }
    setIsUpdating(false);
  };

  const handleQuickContainerUpdate = async () => {
    if (
      !quickContainerInput.trim() &&
      !quickIsoInput.trim() &&
      !quickVehicleInput.trim()
    )
      return;
    setIsQuickUpdating(true);
    try {
      await updateScanStageInfo({
        scan_id:reportData?.scan_id,
        stage_name:reportData?.stage_name,
        container_number:
          quickContainerInput.trim() || reportData?.container_number,
        iso_code: quickIsoInput.trim() || reportData?.iso_code,
        vehicle_no: quickVehicleInput.trim() || reportData?.vehicle_no,
      });
      successMessage("Updated successfully.");
      if (quickContainerInput.trim())
        handleContainerUpdated(quickContainerInput.trim());
      resetQuickForm();
      setOpenContainerEdit(false);
      fetchReportDetails();
    } catch (err) {
      const errMsg =
        err?.response?.data?.message || err?.message || "Save failed";
      errorMessage(errMsg);
    }
    setIsQuickUpdating(false);
  };

  return (
    <>
      {isLoading ? (
        <Loader color="primary" size={40} />
      ) : (
        <div className="flex w-full h-screen overflow-y-scroll bg-background bg-gray-200">
          <div className="flex-1 p-8">
            {/* ── Info card ── */}
            <div className="p-6 bg-white rounded-lg shadow-md">
              <div className="flex items-center gap-3 mb-4">
                <button
                  onClick={() => navigate("/report")}
                  className="text-gray-600 hover:text-black"
                  aria-label="Back"
                >
                  <FaArrowLeft size={18} />
                </button>
                <h1 className="text-xl font-semibold">
                  Exit Survey : {labels.reportTitle}
                </h1>
                {roleName === SUPER_ADMIN && (
                  <button
                    onClick={() => setOpen(true)}
                    title="Edit Survey Info"
                    className="ml-auto flex items-center gap-1.5 text-sm font-medium text-blue-600 border border-blue-200 rounded-lg px-3 py-1.5 hover:bg-blue-50 transition"
                  >
                    <Pencil size={14} /> Edit
                  </button>
                )}
              </div>

              <div className="border rounded p-6 mb-6">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="space-y-4">
                    <div>
                      <span className="font-medium">Report Number : </span>
                      <span>{reportData.report_number}</span>
                    </div>
                    <div>
                      <span className="font-medium">{labels.idLabel} : </span>
                      <span>{reportData.container_number}</span>
                    </div>
                    <div>
                      <span className="font-medium">Survey Date : </span>
                      <span>
                        {new Date(reportData.created_at).toLocaleDateString(
                          "en-GB",
                        )}
                      </span>
                    </div>
                    <div>
                      <span className="font-medium">Carrier type : </span>
                      <span>{reportData.carrier_type?.carriertype || "-"}</span>
                    </div>
                    <div>
                      <span className="font-medium">Captured by : </span>
                      <span>
                        {reportData?.captured_by_first_name}
                        {reportData?.captured_by_last_name
                          ? ` ${reportData.captured_by_last_name}`
                          : ""}
                      </span>
                    </div>
                    <div>
                      <span className="font-medium">Company : </span>
                      <span>{reportData.company_name || "-"}</span>
                    </div>
                    <div>
                      <span className="font-medium">Comment : </span>
                      <span>{reportData?.comment || "-"}</span>
                    </div>
                    <div>
                      <span className="font-medium">Location : </span>
                      <span>{addressData}</span>
                    </div>
                  </div>

                  <div className="space-y-4">
                    <div>
                      <span className="font-medium">Scan ID : </span>
                      <span>{reportData.scan_id}</span>
                    </div>
                    <div>
                      <span className="font-medium">
                        {labels.capacityLabel} :{" "}
                      </span>
                      <span>{reportData.container_capacity}</span>
                    </div>
                    <div>
                      <span className="font-medium">Survey Time : </span>
                      <span>
                        {reportData.created_at
                          ? new Date(reportData.created_at).toLocaleTimeString(
                              "en-US",
                              {
                                hour: "2-digit",
                                minute: "2-digit",
                                hour12: true,
                              },
                            )
                          : "-"}
                      </span>
                    </div>
                    <div>
                      <span className="font-medium">Manager : </span>
                      <span>
                        {reportData?.manager_first_name}
                        {reportData?.manager_last_name &&
                        reportData.manager_last_name !== "null"
                          ? ` ${reportData.manager_last_name}`
                          : ""}
                      </span>
                    </div>
                    <div>
                      <span className="font-medium">Vehicle Number : </span>
                      <span>{reportData.vehicle_no || "-"}</span>
                    </div>
                    <div>
                      <span className="font-medium">Yard : </span>
                      <span>{reportData.yard_name || "-"}</span>
                    </div>
                    <div>
                      <span className="font-medium">{labels.codeLabel} : </span>
                      <span>{reportData?.iso_code || "-"}</span>
                    </div>
                    <div>
                      <span className="font-medium">Max Gross Weight : </span>
                      <span>{reportData.max_gross_weight || "-"}</span>
                    </div>
                  </div>

                  <div className="space-y-4">
                    <div>
                      <span className="font-medium">Stage : </span>
                      <span>{reportData.stage_name}</span>
                    </div>
                    <div>
                      <span className="font-medium">{labels.typeLabel} : </span>
                      <span>
                        {reportData.container_type?.containertype || "-"}
                      </span>
                    </div>
                    <div>
                      <span className="font-medium">Entry Date : </span>
                      <span>{reportData.entry_time || "-"}</span>
                    </div>
                    <div>
                      <span className="font-medium">Source : </span>
                      <span>{reportData.camera_name}</span>
                    </div>
                    <div>
                      <span className="font-medium">Load Status : </span>
                      <span>{reportData.load_status?.loadstatus || "-"}</span>
                    </div>
                    <div>
                      <span className="font-medium">Contact : </span>
                      <span>{reportData.captured_contact}</span>
                    </div>
                    <div>
                      <span className="font-medium">Shipping Line : </span>
                      <span>{reportData.shipping_line_name || "-"}</span>
                    </div>
                    <div>
                      <span className="font-medium">Status : </span>
                      <span>
                        {reportData.report_published
                          ? "Approved"
                          : reportData.reported_to_manager
                            ? "To Be Approved"
                            : "To Be Reviewed"}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* ── Images / Video card ── */}
            <div className="p-6 bg-white rounded-lg shadow-md my-5">
              <FormControl component="fieldset" className="mb-4 w-full">
                <div className="flex items-center w-full">
                  <RadioGroup
                    row
                    value={viewType}
                    onChange={(e) => setViewType(e.target.value)}
                  >
                    <FormControlLabel
                      value="images"
                      control={<Radio />}
                      label="Images"
                    />
                    <FormControlLabel
                      value="video"
                      control={<Radio />}
                      label="Video"
                    />
                  </RadioGroup>

                  <div className="ml-auto flex items-center gap-2">
                    {(roleName === DASHBOARD_EDITOR ||
                      roleName === SUPER_ADMIN ||
                      roleName === VISO_SUPER_ADMIN) &&
                      reportData?.dashboard_info_edit_count < 1 && (
                        <Button
                          label={`Edit Scan info`}
                          onClick={() => setOpenContainerEdit(true)}
                          variant="outlined"
                          className="text-sm flex-1 sm:flex-none text-blue-700 border-blue-300 hover:bg-gray-100"
                        />
                      )}
                    {viewType === "images" && (
                      <button
                        title="View images"
                        onClick={() => setSlideViewOpen(true)}
                        className="w-8 h-8 flex items-center justify-center rounded border border-blue-600 text-blue-600 hover:border-blue-400 hover:bg-blue-50 transition"
                      >
                        <svg
                          width="16"
                          height="16"
                          viewBox="0 0 16 16"
                          fill="none"
                        >
                          <rect
                            x="1"
                            y="1"
                            width="6"
                            height="6"
                            rx="1.2"
                            fill="currentColor"
                          />
                          <rect
                            x="9"
                            y="1"
                            width="6"
                            height="6"
                            rx="1.2"
                            fill="currentColor"
                          />
                          <rect
                            x="1"
                            y="9"
                            width="6"
                            height="6"
                            rx="1.2"
                            fill="currentColor"
                          />
                          <rect
                            x="9"
                            y="9"
                            width="6"
                            height="6"
                            rx="1.2"
                            fill="currentColor"
                          />
                        </svg>
                      </button>
                    )}
                  </div>
                </div>
                {viewType === "images" &&
                  (roleName === DASHBOARD_EDITOR ||
                    roleName === SUPER_ADMIN) && (
                    <span className="text-xs italic ml-4">
                      ℹ️ Info: Open a{" "}
                      <span className="font-medium">Processed</span> image and
                      click <span className="font-medium">Draw Defects</span> to
                      add, edit, or remove defects.
                    </span>
                  )}
                {viewType === "images" && (
                  <div className="flex gap-4 mt-2 ml-4 flex-wrap">
                    {[
                      { key: "raw", label: "Raw" },
                      { key: "processed", label: "Processed" },
                    ].map(({ key, label }) => (
                      <button
                        key={key}
                        onClick={() => handleImageTypeTab(key)}
                        className={`focus:outline-none pb-2 px-1 capitalize whitespace-nowrap ${imageType === key ? "border-b-2 border-blue-600 text-blue-600" : "text-gray-600"}`}
                      >
                        {label}
                      </button>
                    ))}
                    {compressedHeatmapImages.length > 0 && (
                      <button
                        onClick={() => handleImageTypeTab("heatmap")}
                        className={`focus:outline-none pb-2 px-1 capitalize whitespace-nowrap ${imageType === "heatmap" ? "border-b-2 border-blue-600 text-blue-600" : "text-gray-600"}`}
                      >
                        Heatmap
                      </button>
                    )}
                    {compressedSegmentedImages.length > 0 && (
                      <button
                        onClick={() => handleImageTypeTab("segmented")}
                        className={`focus:outline-none pb-2 px-1 capitalize whitespace-nowrap ${imageType === "segmented" ? "border-b-2 border-blue-600 text-blue-600" : "text-gray-600"}`}
                      >
                        Segmented
                      </button>
                    )}
                    {compressedContainerNumberImages.length > 0 && (
                      <button
                        onClick={() =>
                          handleImageTypeTab(
                            assetType === "IsoTank"
                              ? "tank_number"
                              : "container_number",
                          )
                        }
                        className={`focus:outline-none pb-2 px-1 capitalize whitespace-nowrap ${imageType === "container_number" || imageType === "tank_number" ? "border-b-2 border-blue-600 text-blue-600" : "text-gray-600"}`}
                      >
                        {`${labels.numberLabel} Number`}
                      </button>
                    )}
                    {compressedVehicleNumberImages.length > 0 && (
                      <button
                        onClick={() => handleImageTypeTab("vehicle_number")}
                        className={`focus:outline-none pb-2 px-1 capitalize whitespace-nowrap ${
                          imageType === "vehicle_number"
                            ? "border-b-2 border-blue-600 text-blue-600"
                            : "text-gray-600"
                        }`}
                      >
                        Vehicle Number
                      </button>
                    )}
                    {compressedIsoNumberImages.length > 0 && (
                      <button
                        onClick={() => handleImageTypeTab("iso_number")}
                        className={`focus:outline-none pb-2 px-1 capitalize whitespace-nowrap ${imageType === "iso_number" ? "border-b-2 border-blue-600 text-blue-600" : "text-gray-600"}`}
                      >
                        ISO Number
                      </button>
                    )}
                    {compressedPlateImages.length > 0 && (
                      <button
                        onClick={() => handleImageTypeTab("plate")}
                        className={`focus:outline-none pb-2 px-1 capitalize whitespace-nowrap ${imageType === "plate" ? "border-b-2 border-blue-600 text-blue-600" : "text-gray-600"}`}
                      >
                        Plate
                      </button>
                    )}
                    {compressedSealCodeImages.length > 0 && (
                      <button
                        onClick={() => handleImageTypeTab("seal_code")}
                        className={`focus:outline-none pb-2 px-1 capitalize whitespace-nowrap ${imageType === "seal_code" ? "border-b-2 border-blue-600 text-blue-600" : "text-gray-600"}`}
                      >
                        Seal Code
                      </button>
                    )}
                  </div>
                )}
              </FormControl>
              <MediaViewer
                images={getCurrentImages()}
                video={reportData.video}
                viewType={viewType}
                imageType={imageType}
                roleName={roleName}
                scanStageId={reportData?.scan_stage_id}
                containerNumber={reportData?.container_number}
                isoCode={reportData?.iso_code}
                scanId={scanId}
                existingDefects={reportData.defects}
                rawImages={compressedRawImages}
                reportData={reportData}
                onDefectsUpdated={handleDefectsUpdated}
                onContainerUpdated={handleContainerUpdated}
              />
              <SlideViewModal
                open={slideViewOpen}
                onClose={() => setSlideViewOpen(false)}
                allImages={reportData.scan_images || []}
              />
            </div>

            {/* ── Defects card ── */}
            <div className="p-6 bg-white rounded-lg shadow-md my-5">
              <div className="border-b mb-6">
                <div className="flex gap-4">
                  <button
                    onClick={() => handleTabChange(false)}
                    className={`focus:outline-none pb-2 px-1 ${insignificant === false ? "border-b-2 border-blue-600 text-blue-600" : "text-gray-600"}`}
                  >
                    Defect to be considered
                  </button>
                  <button
                    onClick={() => handleTabChange(true)}
                    className={`focus:outline-none pb-2 px-1 ${insignificant === true ? "border-b-2 border-blue-600 text-blue-600" : "text-gray-600"}`}
                  >
                    Insignificant defects
                  </button>
                </div>
              </div>

              <div className="flex flex-col gap-6">
                <div className="flex flex-col sm:flex-row gap-4 justify-between items-start sm:items-center">
                  <div className="flex gap-2 w-full sm:w-auto flex-wrap">
                    <Button
                      onClick={downloadReport}
                      label="DOWNLOAD REPORT"
                      icon={<Download size={15} />}
                      iconPosition="end"
                      variant="outlined"
                      className="text-sm flex-1 sm:flex-none text-gray-700 border-gray-300 hover:bg-gray-100"
                    />
                    <Button
                      label="Generate Estimate"
                      onClick={() => setEstimateOpen(true)}
                      icon={<FileText size={15} />}
                      iconPosition="end"
                      variant="outlined"
                      className="text-sm flex-1 sm:flex-none text-gray-700 border-gray-300 hover:bg-gray-100"
                    />
                    {roleName === APPUSER &&
                      !reportData?.reported_to_manager &&
                      !reportData?.report_published && (
                        <Button
                          onClick={handleReportToManager}
                          label="REPORT TO MANAGER"
                          disabled={
                            reportData?.report_published ||
                            reportData?.captured_by_id !== UserId
                          }
                          variant="outlined"
                          color="action"
                          className="text-sm flex-1 sm:flex-none text-gray-700 border-gray-300 hover:bg-gray-100"
                        />
                      )}
                    {roleName === MANAGER && !reportData?.report_published && (
                      <Button
                        onClick={handlePublishReport}
                        label="PUBLISH THE REPORT"
                        variant="outlined"
                        color="action"
                        disabled={
                          (reportData?.manager_id !== null &&
                            reportData?.manager_id !== UserId) ||
                          (reportData?.manager_id === null &&
                            reportData?.captured_by_id !== UserId)
                        }
                        className="text-sm flex-1 sm:flex-none text-gray-700 border-gray-300 hover:bg-gray-100"
                      />
                    )}
                    {roleName !== ADMIN && (
                      <Button
                        onClick={moveToInsignificant}
                        label={`MOVE TO ${insignificant ? "SIGNIFICANT" : "INSIGNIFICANT"} DEFECT`}
                        icon={<ArrowRight size={15} />}
                        disabled={
                          reportData?.report_published ||
                          !currentDefects.some((d) => d.isChecked)
                        }
                        iconPosition="end"
                        variant="outlined"
                        color="primary"
                        className="text-sm flex-1 sm:flex-none"
                      />
                    )}
                    <Button
                      onClick={() => shareReport(reportData)}
                      label={isSharing ? "Sharing..." : "Share Report"}
                      icon={
                        isSharing ? (
                          <svg
                            className="animate-spin h-4 w-4"
                            viewBox="0 0 24 24"
                          >
                            <circle
                              className="opacity-25"
                              cx="12"
                              cy="12"
                              r="10"
                              stroke="currentColor"
                              strokeWidth="4"
                              fill="none"
                            />
                            <path
                              className="opacity-75"
                              fill="currentColor"
                              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                            />
                          </svg>
                        ) : (
                          <Share2 size={15} />
                        )
                      }
                      iconPosition="end"
                      variant="outlined"
                      disabled={isSharing}
                      className="text-sm flex-1 sm:flex-none text-gray-700 border-gray-300 hover:bg-gray-100"
                    />
                  </div>
                </div>

                <div className="border rounded-lg overflow-x-auto">
                  <table className="min-w-full">
                    <thead>
                      <tr className="bg-gray-50 border-b">
                        <th className="w-12 px-4 py-3">
                          <input
                            onChange={(e) =>
                              setCheckAll(e.currentTarget.checked)
                            }
                            checked={checkAll}
                            type="checkbox"
                            className="rounded border-gray-300"
                          />
                        </th>
                        <th className="px-4 py-3 text-left font-medium text-sm">
                          Defect ID
                        </th>
                        <th className="px-4 py-3 text-left font-medium text-sm">
                          Defect Type
                        </th>
                        <th className="px-4 py-3 text-left font-medium text-sm">
                          Defect Area (cm²)
                        </th>
                        <th className="px-4 py-3 text-left font-medium text-sm">
                          Defect Side
                        </th>
                        <th className="px-4 py-3 text-left font-medium text-sm">
                          Image Number
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {currentDefects.map((defect, index) => (
                        <tr key={index} className="border-b">
                          <td className="px-4 py-3">
                            <input
                              type="checkbox"
                              onChange={(e) =>
                                handleCheck(
                                  defect.defect_id,
                                  e.currentTarget.checked,
                                )
                              }
                              checked={defect.isChecked}
                              className="rounded border-gray-300"
                            />
                          </td>
                          <td className="px-4 py-3">{defect.defect_no}</td>
                          <td className="px-4 py-3">{defect.defect_name}</td>
                          <td className="px-4 py-3">
                            {defect.defect_dimension?.area_cm2 > 0
                              ? defect.defect_dimension.area_cm2
                              : "-"}
                          </td>
                          <td className="px-4 py-3">
                            {defect.defect_side &&
                            defect.defect_side !== "UNKNOWN"
                              ? defect.defect_side
                              : "-"}
                          </td>
                          <td className="px-4 py-3">{defect.image_no}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ── Edit Survey Info Dialog ── */}
      <Dialog
        open={open}
        onClose={() => {
          setOpen(false);
          resetForm();
        }}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { borderRadius: "14px", overflow: "hidden" } }}
      >
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100">
          <div>
            <p className="text-base font-semibold text-gray-800">
              Edit Survey Info
            </p>
            <p className="text-xs text-gray-400 mt-0.5">
              Super Admin · Update scan details
            </p>
          </div>
          <IconButton
            onClick={() => {
              setOpen(false);
              resetForm();
            }}
            size="small"
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </div>

        {roleName === SUPER_ADMIN && (
          <div className="flex flex-col gap-4 px-5 py-5">
            <div className="flex gap-3">
              <div className="flex flex-col gap-1 flex-1">
                <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide">
                  {labels.idLabel}
                </label>
                <p className="text-xs text-gray-400 mb-1">
                  Current:{" "}
                  <span className="font-semibold text-gray-600">
                    {reportData?.container_number || "N/A"}
                  </span>
                </p>
                <input
                  type="text"
                  value={containerInput}
                  onChange={handleContainerInputChange}
                  placeholder="ABCD1234567"
                  className="border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-100 bg-gray-50 font-mono tracking-widest"
                />
              </div>
              <div className="flex flex-col gap-1 flex-1">
                <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide">
                  {labels.codeLabel}
                </label>
                <p className="text-xs text-gray-400 mb-1">
                  Current:{" "}
                  <span className="font-semibold text-gray-600">
                    {reportData?.iso_code || "N/A"}
                  </span>
                </p>
                <input
                  type="text"
                  value={isoInput}
                  onChange={handleIsoInputChange}
                  placeholder="e.g. 22G1"
                  className="border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-100 bg-gray-50"
                />
              </div>
            </div>

            <div className="flex gap-3">
              <div className="flex flex-col gap-1 flex-1">
                <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide">
                  Stage Name
                </label>
                <p className="text-xs text-gray-400 mb-1">
                  Current:{" "}
                  <span className="font-semibold text-gray-600">
                    {reportData?.stage_name || "N/A"}
                  </span>
                </p>
                <select
                  value={stageInput}
                  onChange={(e) => setStageInput(e.target.value)}
                  className="border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-100 bg-gray-50"
                >
                  <option value="">— Select stage —</option>
                  {["At Entry", "At Exit"].map((s) => (
                    <option key={s} value={s}>
                      {s}
                    </option>
                  ))}
                </select>
              </div>
              <div className="flex flex-col gap-1 flex-1">
                <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide">
                  {labels.capacityLabel}
                </label>
                <p className="text-xs text-gray-400 mb-1">
                  Current:{" "}
                  <span className="font-semibold text-gray-600">
                    {reportData?.container_capacity || "N/A"}
                  </span>
                </p>
                <select
                  value={capacityInput}
                  onChange={(e) => setCapacityInput(e.target.value)}
                  className="border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-100 bg-gray-50"
                >
                  <option value="">— Select capacity —</option>
                  {["20' HD", "40' HD", "40' HC", "Others"].map((c) => (
                    <option key={c} value={c}>
                      {c}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div className="flex gap-3">
              <div className="flex flex-col gap-1 flex-1">
                <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide">
                  Vehicle No
                </label>
                <p className="text-xs text-gray-400 mb-1">
                  Current:{" "}
                  <span className="font-semibold text-gray-600">
                    {reportData?.vehicle_no || "N/A"}
                  </span>
                </p>
                <input
                  type="text"
                  value={vehicleNoInput}
                  onChange={(e) => setVehicleNoInput(e.target.value)}
                  placeholder="e.g. UP32AB1234"
                  className="border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-100 bg-gray-50"
                />
              </div>
              <div className="flex flex-col gap-1 flex-1">
                <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide">
                  Max Gross Weight
                </label>
                <p className="text-xs text-gray-400 mb-1">
                  Current:{" "}
                  <span className="font-semibold text-gray-600">
                    {reportData?.max_gross_weight || "N/A"}
                  </span>
                </p>
                <div className="relative">
                  <input
                    type="text"
                    value={maxGrossWeightInput}
                    onChange={(e) => setMaxGrossWeightInput(e.target.value)}
                    placeholder="e.g. 30,480 kg"
                    className="border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-100 bg-gray-50 w-full"
                  />
                </div>
                <p className="text-xs text-gray-400 mt-0.5">
                  22G1 → 30,480 kg · 45G1 → 32,500 kg · 45R1 → 35,000 kg
                </p>
              </div>
            </div>

            <div className="flex items-center justify-end gap-2 pt-1 border-t border-gray-100 mt-1">
              <button
                onClick={() => {
                  setOpen(false);
                  resetForm();
                }}
                className="px-4 py-2 rounded-lg text-sm font-medium text-gray-500 hover:bg-gray-100 transition"
              >
                Cancel
              </button>
              <button
                onClick={handleupdateScanInfo}
                disabled={
                  isUpdating ||
                  (containerInput.length === 0 &&
                    isoInput.trim().length === 0 &&
                    stageInput.length === 0 &&
                    capacityInput.length === 0 &&
                    vehicleNoInput.trim().length === 0 &&
                    maxGrossWeightInput.trim().length === 0)
                }
                className="px-5 py-2 rounded-lg text-sm font-semibold bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-40 disabled:cursor-not-allowed transition"
              >
                {isUpdating ? "Updating..." : "Update"}
              </button>
            </div>
          </div>
        )}
      </Dialog>

      <Snackbar
        open={snackbarOpen}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
        message={"Downloading..."}
        key="topright"
      />

      <RepairEstimateModal
        open={estimateOpen}
        onClose={() => setEstimateOpen(false)}
        defects={defects}
        reportData={reportData}
      />
      {/* ── Quick Edit: Container / ISO / Vehicle Dialog ── */}
      <Dialog
        open={openContainerEdit}
        onClose={() => {
          setOpenContainerEdit(false);
          resetQuickForm();
        }}
        maxWidth="xs"
        fullWidth
        PaperProps={{ sx: { borderRadius: "14px", overflow: "hidden" } }}
      >
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100">
          <div>
            <p className="text-base font-semibold text-gray-800">
              Edit Scan Info
            </p>
            <p className="text-xs text-gray-400 mt-0.5">
              Update container · ISO · vehicle
            </p>
          </div>
          <IconButton
            onClick={() => {
              setOpenContainerEdit(false);
              resetQuickForm();
            }}
            size="small"
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </div>

        <div className="flex flex-col gap-4 px-5 py-5">
          {/* Container Number */}
          <div className="flex flex-col gap-1">
            <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide">
              {labels.idLabel}
            </label>
            <p className="text-xs text-gray-400 mb-1">
              Current:{" "}
              <span className="font-semibold text-gray-600">
                {reportData?.container_number || "-"}
              </span>
            </p>
            <input
              type="text"
              value={quickContainerInput}
              onChange={(e) => {
                const val = e.target.value.toUpperCase();
                if (val.length > 11) return;
                if (val.length <= 4) {
                  if (/^[A-Z]*$/.test(val)) setQuickContainerInput(val);
                } else {
                  const letters = val.slice(0, 4),
                    nums = val.slice(4);
                  if (/^[A-Z]{4}$/.test(letters) && /^[0-9]*$/.test(nums))
                    setQuickContainerInput(val);
                }
              }}
              placeholder="ABCD1234567"
              className="border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-100 bg-gray-50 font-mono tracking-widest"
            />
          </div>

          {/* ISO Code */}
          <div className="flex flex-col gap-1">
            <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide">
              {labels.codeLabel}
            </label>
            <p className="text-xs text-gray-400 mb-1">
              Current:{" "}
              <span className="font-semibold text-gray-600">
                {reportData?.iso_code || "-"}
              </span>
            </p>
            <input
              type="text"
              value={quickIsoInput}
              onChange={(e) => {
                const v = e.target.value.toUpperCase();
                if (v.length <= 4 && /^[A-Z0-9]*$/.test(v)) setQuickIsoInput(v);
              }}
              placeholder={labels.codePlaceholder}
              className="border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-100 bg-gray-50"
            />
          </div>

          {/* Vehicle No */}
          <div className="flex flex-col gap-1">
            <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide">
              Vehicle No
            </label>
            <p className="text-xs text-gray-400 mb-1">
              Current:{" "}
              <span className="font-semibold text-gray-600">
                {reportData?.vehicle_no || "-"}
              </span>
            </p>
            <input
              type="text"
              value={quickVehicleInput}
              onChange={(e) => setQuickVehicleInput(e.target.value)}
              placeholder="e.g. UP32AB1234"
              className="border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-100 bg-gray-50"
            />
          </div>

          <div className="flex items-center justify-end gap-2 pt-1 border-t border-gray-100 mt-1">
            <button
              onClick={() => {
                setOpenContainerEdit(false);
                resetQuickForm();
              }}
              className="px-4 py-2 rounded-lg text-sm font-medium text-gray-500 hover:bg-gray-100 transition"
            >
              Cancel
            </button>
            <button
              onClick={() => setConfirmQuickUpdateOpen(true)}
              disabled={
                isQuickUpdating ||
                (!quickContainerInput.trim() &&
                  !quickIsoInput.trim() &&
                  !quickVehicleInput.trim())
              }
              className="px-5 py-2 rounded-lg text-sm font-semibold bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-40 disabled:cursor-not-allowed transition"
            >
              {isQuickUpdating ? "Updating..." : "Update"}
            </button>
          </div>
        </div>
      </Dialog>
      <AlertDialogModal
        open={confirmQuickUpdateOpen}
        onClose={() => setConfirmQuickUpdateOpen(false)}
        onConfirm={async () => {
          await handleQuickContainerUpdate();
          setConfirmQuickUpdateOpen(false);
        }}
        title="Confirm Update"
        message={
          <span>
            Are you sure you want to update the following:{" "}
            {[
              quickContainerInput.trim() && (
                <span key="container">
                  {labels.idLabel.toLowerCase()} to{" "}
                  <strong>"{quickContainerInput.trim()}"</strong>
                </span>
              ),
              quickIsoInput.trim() && (
                <span key="iso">
                  {labels.codeLabel.toLowerCase()} to{" "}
                  <strong>"{quickIsoInput.trim()}"</strong>
                </span>
              ),
              quickVehicleInput.trim() && (
                <span key="vehicle">
                  vehicle number to{" "}
                  <strong>"{quickVehicleInput.trim()}"</strong>
                </span>
              ),
            ]
              .filter(Boolean)
              .reduce((acc, curr, idx) => {
                if (idx === 0) return [curr];
                return [...acc, ", ", curr];
              }, [])}
            . This action will overwrite the existing details.
          </span>
        }
        confirmButtonText={isQuickUpdating ? "Updating..." : "Yes, Update"}
        cancelButtonText="Cancel"
      />
    </>
  );
};

export default ReportDetails;
